// web/src/screens/admin/settings.ts — operator settings (the v1 /admin/settings).
//
// Two sections:
//   1. LLM call logging — a master toggle. When ON, the turn handler records
//      llm_call / stt_call rows + on-disk request/response payload files for
//      every turn (visible on the attempt-detail screen). Turning it OFF
//      purges the retained payloads + rows.
//   2. Cost pricing — the six per-million-token / per-minute microUSD rates
//      that feed the per-attempt cost telemetry.
//
// All API/user text via textContent only (T-11-09a).

import { renderAdminShell, showAdminToast } from "./admin_shell.js";
import { adminGet, adminPut, adminImportContentBundle, AdminError } from "../../api/admin.js";
import type {
  AdminLLMLoggingConfigDTO,
  AdminPerfTrackingConfigDTO,
  AdminSpeculativeEvalConfigDTO,
  AdminWordMatchConfigDTO,
  AdminPricingConfigDTO,
  AdminUserPromptConfigDTO,
  AdminSystemPromptConfigDTO,
  AdminOpenAISTTPromptConfigDTO,
} from "../../types/api.js";
import { section, errMessage } from "./logs_common.js";
import { USER_PROMPT_VARS, promptVarsInfoBox, makePromptTextarea } from "./prompt_vars.js";

export function mount(root: HTMLElement, _props?: unknown): () => void {
  const main = renderAdminShell(root, [{ label: "Settings" }]);

  const status = document.createElement("p");
  status.textContent = "Loading…";
  status.setAttribute("style", "color: #6b7280; font-size: 14px;");
  main.appendChild(status);

  // ── LLM call logging toggle ───────────────────────────────────────────
  const logBody = section(main, "LLM call logging");
  const logRow = document.createElement("label");
  logRow.setAttribute("style", "display: flex; align-items: center; gap: 10px; font-size: 14px; cursor: pointer;");
  const logCheckbox = document.createElement("input");
  logCheckbox.type = "checkbox";
  logCheckbox.disabled = true;
  const logLabelText = document.createElement("span");
  logLabelText.textContent = "Record per-turn LLM + STT call payloads (llm_logging_enabled)";
  logRow.appendChild(logCheckbox);
  logRow.appendChild(logLabelText);
  logBody.appendChild(logRow);
  const logNote = document.createElement("p");
  logNote.textContent = "When enabled, each turn writes llm_call / stt_call rows and request/response payload files (audio bytes redacted). Turning it OFF deletes the retained payload files and rows.";
  logNote.setAttribute("style", "color: #6b7280; font-size: 12px; margin: 8px 0 0 0;");
  logBody.appendChild(logNote);

  async function onToggleLogging(): Promise<void> {
    const want = logCheckbox.checked;
    logCheckbox.disabled = true;
    try {
      const saved = await adminPut<AdminLLMLoggingConfigDTO, AdminLLMLoggingConfigDTO>("/api/admin/config/llm-logging", { enabled: want });
      logCheckbox.checked = saved.enabled;
      showAdminToast(document.body, saved.enabled ? "LLM call logging enabled" : "LLM call logging disabled — payloads purged");
    } catch (err) {
      logCheckbox.checked = !want; // revert optimistic flip
      showAdminToast(document.body, errMessage(err, "update llm-logging"));
    } finally {
      logCheckbox.disabled = false;
    }
  }
  logCheckbox.addEventListener("change", onToggleLogging);

  // ── Performance tracking toggle (Phase 2) ─────────────────────────────
  const perfBody = section(main, "Performance tracking");
  const perfRow = document.createElement("label");
  perfRow.setAttribute("style", "display: flex; align-items: center; gap: 10px; font-size: 14px; cursor: pointer;");
  const perfCheckbox = document.createElement("input");
  perfCheckbox.type = "checkbox";
  perfCheckbox.disabled = true;
  const perfLabelText = document.createElement("span");
  perfLabelText.textContent = "Persist per-turn latency metrics (perf_tracking_enabled)";
  perfRow.appendChild(perfCheckbox);
  perfRow.appendChild(perfLabelText);
  perfBody.appendChild(perfRow);
  const perfNote = document.createElement("p");
  perfNote.textContent = "When enabled, each turn stores its server time and the browser beacons round-trip, network, audio and perceived/pipeline latency onto the attempt row (shown on the attempt-detail screen). When OFF, the browser sends nothing and the server stores nothing. The Server-Timing header and devtools console breakdown are always available regardless.";
  perfNote.setAttribute("style", "color: #6b7280; font-size: 12px; margin: 8px 0 0 0;");
  perfBody.appendChild(perfNote);

  async function onTogglePerf(): Promise<void> {
    const want = perfCheckbox.checked;
    perfCheckbox.disabled = true;
    try {
      const saved = await adminPut<AdminPerfTrackingConfigDTO, AdminPerfTrackingConfigDTO>("/api/admin/config/perf-tracking", { enabled: want });
      perfCheckbox.checked = saved.enabled;
      showAdminToast(document.body, saved.enabled ? "Performance tracking enabled" : "Performance tracking disabled");
    } catch (err) {
      perfCheckbox.checked = !want; // revert optimistic flip
      showAdminToast(document.body, errMessage(err, "update perf-tracking"));
    } finally {
      perfCheckbox.disabled = false;
    }
  }
  perfCheckbox.addEventListener("change", onTogglePerf);

  // ── Speculative evaluation toggle (streaming; migration 0048) ─────────
  const specBody = section(main, "Speculative evaluation (streaming)");
  const specRow = document.createElement("label");
  specRow.setAttribute("style", "display: flex; align-items: center; gap: 10px; font-size: 14px; cursor: pointer;");
  const specCheckbox = document.createElement("input");
  specCheckbox.type = "checkbox";
  specCheckbox.disabled = true;
  const specLabelText = document.createElement("span");
  specLabelText.textContent = "Evaluate during the end-of-speech hold (speculative_eval_enabled)";
  specRow.appendChild(specCheckbox);
  specRow.appendChild(specLabelText);
  specBody.appendChild(specRow);
  const specNote = document.createElement("p");
  specNote.textContent = "Streaming STT only. When enabled, the LLM evaluation starts on the interim transcript while the child is still in the ~2 s end-of-speech silence hold, so its round-trip overlaps the wait. The result is reused only if the authoritative finalize produces identical inputs — otherwise the turn re-evaluates — so this never changes the evaluation, only the latency. No effect on the batch path or when streaming is off.";
  specNote.setAttribute("style", "color: #6b7280; font-size: 12px; margin: 8px 0 0 0;");
  specBody.appendChild(specNote);

  async function onToggleSpec(): Promise<void> {
    const want = specCheckbox.checked;
    specCheckbox.disabled = true;
    try {
      const saved = await adminPut<AdminSpeculativeEvalConfigDTO, AdminSpeculativeEvalConfigDTO>("/api/admin/config/speculative-eval", { enabled: want });
      specCheckbox.checked = saved.enabled;
      showAdminToast(document.body, saved.enabled ? "Speculative evaluation enabled" : "Speculative evaluation disabled");
    } catch (err) {
      specCheckbox.checked = !want; // revert optimistic flip
      showAdminToast(document.body, errMessage(err, "update speculative-eval"));
    } finally {
      specCheckbox.disabled = false;
    }
  }
  specCheckbox.addEventListener("change", onToggleSpec);

  // ── Word-match auto-accept toggle (migration 0050) ────────────────────
  const wmBody = section(main, "Word-match auto-accept");
  const wmRow = document.createElement("label");
  wmRow.setAttribute("style", "display: flex; align-items: center; gap: 10px; font-size: 14px; cursor: pointer;");
  const wmCheckbox = document.createElement("input");
  wmCheckbox.type = "checkbox";
  wmCheckbox.disabled = true;
  const wmLabelText = document.createElement("span");
  wmLabelText.textContent = "Accept when any single spoken word matches an answer (word_match_enabled)";
  wmRow.appendChild(wmCheckbox);
  wmRow.appendChild(wmLabelText);
  wmBody.appendChild(wmRow);
  const wmNote = document.createElement("p");
  wmNote.textContent = "When a transcript already contains the expected answer, the turn is marked correct and the LLM call is skipped entirely (faster, cheaper). EXACT match — the whole utterance equals an accepted answer — is always on and needs no toggle, because it can only ratify a turn the evaluator would call correct anyway. This toggle adds the WORD pass: any single spoken word equal to an accepted answer accepts the turn (e.g. \"tas ir ābols\" → the answer \"ābols\"). It deliberately over-accepts, which favours the child feeling successful; turn it off for stricter, exact-only auto-accept. Matching is case- and accent-insensitive. No effect on multi-word answers (those still require an exact match).";
  wmNote.setAttribute("style", "color: #6b7280; font-size: 12px; margin: 8px 0 0 0;");
  wmBody.appendChild(wmNote);

  async function onToggleWordMatch(): Promise<void> {
    const want = wmCheckbox.checked;
    wmCheckbox.disabled = true;
    try {
      const saved = await adminPut<AdminWordMatchConfigDTO, AdminWordMatchConfigDTO>("/api/admin/config/word-match", { enabled: want });
      wmCheckbox.checked = saved.enabled;
      showAdminToast(document.body, saved.enabled ? "Word-match auto-accept enabled" : "Word-match auto-accept disabled");
    } catch (err) {
      wmCheckbox.checked = !want; // revert optimistic flip
      showAdminToast(document.body, errMessage(err, "update word-match"));
    } finally {
      wmCheckbox.disabled = false;
    }
  }
  wmCheckbox.addEventListener("change", onToggleWordMatch);

  // ── Content I/O bundle (xlsx + images zip) ──────────────────────────
  // Export streams a zip with content.xlsx + content/images/**. Import
  // accepts the same shape; it extracts images first, then runs the
  // atomic excelio importer over the workbook.
  const ioBody = section(main, "Content I/O (Excel + images zip)");
  const ioDesc = document.createElement("p");
  ioDesc.textContent = "Export downloads a zip containing the full content workbook (content.xlsx) and every file under content/images. Import accepts the same zip — images are extracted to disk and the workbook is imported in one transaction. TTS audio is not bundled; rebuild it after a strict-replace import.";
  ioDesc.setAttribute("style", "color: #6b7280; font-size: 12px; margin: 0 0 12px 0;");
  ioBody.appendChild(ioDesc);

  const ioRow = document.createElement("div");
  ioRow.setAttribute("style", "display: flex; flex-wrap: wrap; gap: 12px; align-items: center;");
  ioBody.appendChild(ioRow);

  const exportLink = document.createElement("a");
  exportLink.textContent = "Download bundle (.zip)";
  exportLink.setAttribute("href", "/api/admin/content/export.zip");
  exportLink.setAttribute("style", "padding: 8px 14px; background: #2563eb; color: white; border: 0; border-radius: 6px; text-decoration: none; font-size: 14px;");
  ioRow.appendChild(exportLink);

  const importBtn = document.createElement("button");
  importBtn.type = "button";
  importBtn.textContent = "Import bundle…";
  importBtn.setAttribute("style", "padding: 8px 14px; background: #0f766e; color: white; border: 0; border-radius: 6px; cursor: pointer; font-size: 14px;");
  ioRow.appendChild(importBtn);

  const replaceLbl = document.createElement("label");
  replaceLbl.setAttribute("style", "display: flex; align-items: center; gap: 6px; font-size: 13px; color: #374151;");
  const replaceCb = document.createElement("input");
  replaceCb.type = "checkbox";
  replaceLbl.appendChild(replaceCb);
  replaceLbl.appendChild(document.createTextNode("Strict replace (delete rows absent from workbook)"));
  ioRow.appendChild(replaceLbl);

  const skipAudioLbl = document.createElement("label");
  skipAudioLbl.setAttribute("style", "display: flex; align-items: center; gap: 6px; font-size: 13px; color: #374151;");
  const skipAudioCb = document.createElement("input");
  skipAudioCb.type = "checkbox";
  skipAudioLbl.appendChild(skipAudioCb);
  skipAudioLbl.appendChild(document.createTextNode("Skip TTS presence check"));
  ioRow.appendChild(skipAudioLbl);

  const purgeSessionsLbl = document.createElement("label");
  purgeSessionsLbl.setAttribute("style", "display: flex; align-items: center; gap: 6px; font-size: 13px; color: #b91c1c;");
  const purgeSessionsCb = document.createElement("input");
  purgeSessionsCb.type = "checkbox";
  purgeSessionsLbl.appendChild(purgeSessionsCb);
  purgeSessionsLbl.appendChild(document.createTextNode("Purge live sessions (delete attempts for imported games)"));
  purgeSessionsLbl.title = "Deletes every session attached to each imported game before the upsert, cascading to attempts and attempt_audio rows. Use to unblock strict-replace when the importer reports live sessions on a game.";
  ioRow.appendChild(purgeSessionsLbl);

  // Hidden input for the file picker — clicking the visible button opens it.
  const filePicker = document.createElement("input");
  filePicker.type = "file";
  filePicker.accept = ".zip,application/zip";
  filePicker.setAttribute("style", "display: none;");
  ioBody.appendChild(filePicker);

  // Dismissable alert box rendered under the I/O controls. Populated on
  // failed imports with per-row diagnostics from the structured
  // importIssuesResponse envelope. Hidden until something to show.
  const ioAlert = document.createElement("div");
  ioAlert.setAttribute("role", "alert");
  ioAlert.setAttribute("style",
    "display: none; margin-top: 12px; padding: 12px 14px; background: #fef2f2; " +
    "border: 1px solid #fecaca; border-radius: 6px; color: #7f1d1d; font-size: 13px;");
  ioBody.appendChild(ioAlert);

  function clearAlert(): void {
    ioAlert.style.display = "none";
    ioAlert.replaceChildren();
  }

  function showAlert(title: string, issues: ImportIssueDTO[] | null, fallbackBody: string): void {
    ioAlert.replaceChildren();
    const header = document.createElement("div");
    header.setAttribute("style", "display: flex; justify-content: space-between; align-items: flex-start; gap: 12px;");
    const titleEl = document.createElement("strong");
    titleEl.textContent = title;
    titleEl.setAttribute("style", "font-size: 14px;");
    header.appendChild(titleEl);
    const dismissBtn = document.createElement("button");
    dismissBtn.type = "button";
    dismissBtn.textContent = "Dismiss";
    dismissBtn.setAttribute("style",
      "background: transparent; border: 1px solid #b91c1c; color: #7f1d1d; " +
      "padding: 2px 10px; border-radius: 4px; cursor: pointer; font-size: 12px; line-height: 1.4;");
    dismissBtn.addEventListener("click", clearAlert);
    header.appendChild(dismissBtn);
    ioAlert.appendChild(header);

    if (issues && issues.length > 0) {
      const ol = document.createElement("ol");
      ol.setAttribute("style", "margin: 8px 0 0 0; padding-left: 24px;");
      for (const it of issues) {
        const li = document.createElement("li");
        li.setAttribute("style", "margin: 2px 0;");
        li.textContent = formatIssue(it);
        ol.appendChild(li);
      }
      ioAlert.appendChild(ol);
    } else if (fallbackBody) {
      const p = document.createElement("p");
      p.setAttribute("style", "margin: 6px 0 0 0; white-space: pre-wrap;");
      p.textContent = fallbackBody;
      ioAlert.appendChild(p);
    }
    ioAlert.style.display = "block";
  }

  function onImportClick(): void { filePicker.click(); }
  async function onPickFile(): Promise<void> {
    const f = filePicker.files && filePicker.files[0];
    if (!f) return;
    clearAlert();
    importBtn.disabled = true;
    importBtn.textContent = "Importing…";
    try {
      const res = await adminImportContentBundle(f, {
        replace: replaceCb.checked,
        skipAudio: skipAudioCb.checked,
        purgeSessions: purgeSessionsCb.checked,
      });
      const tags = [
        res.replace ? "strict-replace" : "",
        res.purge_sessions ? "sessions purged" : "",
      ].filter(Boolean).join(", ");
      showAdminToast(document.body,
        `Imported ${res.images_extracted} image(s), ${res.xlsx_bytes} workbook bytes${tags ? ` (${tags})` : ""}`,
        4000);
    } catch (err) {
      const parsed = parseAdminImportError(err);
      const title = parsed.title;
      showAlert(title, parsed.issues, parsed.fallback);
    } finally {
      importBtn.disabled = false;
      importBtn.textContent = "Import bundle…";
      filePicker.value = "";
    }
  }
  importBtn.addEventListener("click", onImportClick);
  filePicker.addEventListener("change", onPickFile);

  // ── Default LLM system-prompt template (migration 0035) ─────────────
  const sysPromptBody = section(main, "Default LLM system prompt");
  const sysPromptDesc = document.createElement("p");
  sysPromptDesc.textContent = "The singleton system prompt the turn handler sends as the LLM `instructions` when no per-Game override is set. Cannot be empty — the cascade has no fallback below this row.";
  sysPromptDesc.setAttribute("style", "color: #6b7280; font-size: 12px; margin: 0 0 8px 0;");
  sysPromptBody.appendChild(sysPromptDesc);
  const sysPromptTA = makePromptTextarea("", 10);
  sysPromptTA.disabled = true;
  sysPromptTA.style.width = "100%";
  sysPromptTA.style.boxSizing = "border-box";
  sysPromptBody.appendChild(sysPromptTA);
  const sysPromptSaveBtn = document.createElement("button");
  sysPromptSaveBtn.type = "button";
  sysPromptSaveBtn.textContent = "Save default system prompt";
  sysPromptSaveBtn.disabled = true;
  sysPromptSaveBtn.setAttribute("style", "margin-top: 8px; padding: 8px 14px; background: #2563eb; color: white; border: 0; border-radius: 6px; cursor: pointer; font-size: 14px;");
  sysPromptBody.appendChild(sysPromptSaveBtn);

  async function onSaveSysPrompt(): Promise<void> {
    const tpl = sysPromptTA.value;
    if (tpl.trim() === "") {
      showAdminToast(document.body, "Default system prompt cannot be empty");
      return;
    }
    sysPromptSaveBtn.disabled = true;
    try {
      const saved = await adminPut<AdminSystemPromptConfigDTO, AdminSystemPromptConfigDTO>(
        "/api/admin/config/system-prompt",
        { template: tpl },
      );
      sysPromptTA.value = saved.template;
      showAdminToast(document.body, "Default system prompt saved");
    } catch (err) {
      showAdminToast(document.body, errMessage(err, "save default system prompt"));
    } finally {
      sysPromptSaveBtn.disabled = false;
    }
  }
  sysPromptSaveBtn.addEventListener("click", onSaveSysPrompt);

  // ── Default LLM user-prompt template (migration 0034) ───────────────
  const userPromptBody = section(main, "Default LLM user prompt");
  const userPromptDesc = document.createElement("p");
  userPromptDesc.textContent = "The singleton template the turn handler sends as the LLM user message when no Game/Card/Task override is set. Cannot be empty — the cascade has no fallback below this row.";
  userPromptDesc.setAttribute("style", "color: #6b7280; font-size: 12px; margin: 0 0 8px 0;");
  userPromptBody.appendChild(userPromptDesc);
  const userPromptTA = makePromptTextarea("", 10);
  userPromptTA.disabled = true;
  userPromptTA.style.width = "100%";
  userPromptTA.style.boxSizing = "border-box";
  userPromptBody.appendChild(userPromptTA);
  userPromptBody.appendChild(promptVarsInfoBox(USER_PROMPT_VARS, "User-prompt substitution variables:"));
  const userPromptSaveBtn = document.createElement("button");
  userPromptSaveBtn.type = "button";
  userPromptSaveBtn.textContent = "Save default user prompt";
  userPromptSaveBtn.disabled = true;
  userPromptSaveBtn.setAttribute("style", "margin-top: 8px; padding: 8px 14px; background: #2563eb; color: white; border: 0; border-radius: 6px; cursor: pointer; font-size: 14px;");
  userPromptBody.appendChild(userPromptSaveBtn);

  async function onSaveUserPrompt(): Promise<void> {
    const tpl = userPromptTA.value;
    if (tpl.trim() === "") {
      showAdminToast(document.body, "Default user prompt cannot be empty");
      return;
    }
    userPromptSaveBtn.disabled = true;
    try {
      const saved = await adminPut<AdminUserPromptConfigDTO, AdminUserPromptConfigDTO>(
        "/api/admin/config/user-prompt",
        { template: tpl },
      );
      userPromptTA.value = saved.template;
      showAdminToast(document.body, "Default user prompt saved");
    } catch (err) {
      showAdminToast(document.body, errMessage(err, "save default user prompt"));
    } finally {
      userPromptSaveBtn.disabled = false;
    }
  }
  userPromptSaveBtn.addEventListener("click", onSaveUserPrompt);

  // ── Default OpenAI STT prompt template (migration 0040) ─────────────
  const sttPromptBody = section(main, "Default OpenAI STT prompt");
  const sttPromptDesc = document.createElement("p");
  sttPromptDesc.textContent = "Optional prompt forwarded to OpenAI's Audio Transcriptions API to bias Whisper / gpt-4o-mini-transcribe toward specific vocabulary, spellings, or styles. Used when no per-Game override is set. Leave empty to send no prompt. Substitutes the same variables as the LLM user prompt — {{ChildTranscript}} is always empty at STT time.";
  sttPromptDesc.setAttribute("style", "color: #6b7280; font-size: 12px; margin: 0 0 8px 0;");
  sttPromptBody.appendChild(sttPromptDesc);
  const sttPromptTA = makePromptTextarea("", 6);
  sttPromptTA.disabled = true;
  sttPromptTA.style.width = "100%";
  sttPromptTA.style.boxSizing = "border-box";
  sttPromptBody.appendChild(sttPromptTA);
  sttPromptBody.appendChild(promptVarsInfoBox(USER_PROMPT_VARS, "STT-prompt substitution variables:"));
  const sttPromptSaveBtn = document.createElement("button");
  sttPromptSaveBtn.type = "button";
  sttPromptSaveBtn.textContent = "Save default STT prompt";
  sttPromptSaveBtn.disabled = true;
  sttPromptSaveBtn.setAttribute("style", "margin-top: 8px; padding: 8px 14px; background: #2563eb; color: white; border: 0; border-radius: 6px; cursor: pointer; font-size: 14px;");
  sttPromptBody.appendChild(sttPromptSaveBtn);

  async function onSaveSttPrompt(): Promise<void> {
    sttPromptSaveBtn.disabled = true;
    try {
      const saved = await adminPut<AdminOpenAISTTPromptConfigDTO, AdminOpenAISTTPromptConfigDTO>(
        "/api/admin/config/openai-stt-prompt",
        { template: sttPromptTA.value },
      );
      sttPromptTA.value = saved.template;
      showAdminToast(document.body, "Default STT prompt saved");
    } catch (err) {
      showAdminToast(document.body, errMessage(err, "save default STT prompt"));
    } finally {
      sttPromptSaveBtn.disabled = false;
    }
  }
  sttPromptSaveBtn.addEventListener("click", onSaveSttPrompt);

  // ── Cost pricing form ─────────────────────────────────────────────────
  const priceBody = section(main, "Cost pricing (microUSD)");
  const priceForm = document.createElement("form");
  priceForm.setAttribute("style", "display: grid; grid-template-columns: max-content 160px; gap: 8px 16px; align-items: center; font-size: 14px; margin: 0;");
  const priceFields: Array<{ key: keyof AdminPricingConfigDTO; label: string; input: HTMLInputElement }> = [
    { key: "llm_input_microusd_per_mtok", label: "LLM input / Mtok", input: numInput() },
    { key: "llm_cached_input_microusd_per_mtok", label: "LLM cached input / Mtok", input: numInput() },
    { key: "llm_output_microusd_per_mtok", label: "LLM output / Mtok", input: numInput() },
    { key: "stt_input_microusd_per_mtok", label: "STT input / Mtok", input: numInput() },
    { key: "stt_output_microusd_per_mtok", label: "STT output / Mtok", input: numInput() },
    { key: "stt_microusd_per_minute", label: "STT / minute", input: numInput() },
    { key: "stt_whisper_microusd_per_minute", label: "STT Whisper / minute", input: numInput() },
  ];
  for (const f of priceFields) {
    const lbl = document.createElement("label");
    lbl.textContent = f.label;
    lbl.setAttribute("style", "color: #374151;");
    priceForm.appendChild(lbl);
    priceForm.appendChild(f.input);
  }
  const saveBtn = document.createElement("button");
  saveBtn.type = "submit";
  saveBtn.textContent = "Save pricing";
  saveBtn.disabled = true;
  saveBtn.setAttribute("style", "grid-column: 1 / -1; justify-self: start; margin-top: 8px; padding: 8px 14px; background: #2563eb; color: white; border: 0; border-radius: 6px; cursor: pointer; font-size: 14px;");
  priceForm.appendChild(saveBtn);
  priceBody.appendChild(priceForm);

  function fillPricing(p: AdminPricingConfigDTO): void {
    for (const f of priceFields) f.input.value = String(p[f.key]);
  }
  async function onSavePricing(e: Event): Promise<void> {
    e.preventDefault();
    const payload = {} as AdminPricingConfigDTO;
    for (const f of priceFields) {
      const n = Number.parseInt(f.input.value, 10);
      if (!Number.isFinite(n) || n < 0) {
        showAdminToast(document.body, `${f.label}: must be a non-negative integer`);
        return;
      }
      payload[f.key] = n;
    }
    saveBtn.disabled = true;
    try {
      const saved = await adminPut<AdminPricingConfigDTO, AdminPricingConfigDTO>("/api/admin/config/pricing", payload);
      fillPricing(saved);
      showAdminToast(document.body, "Pricing saved");
    } catch (err) {
      showAdminToast(document.body, errMessage(err, "save pricing"));
    } finally {
      saveBtn.disabled = false;
    }
  }
  priceForm.addEventListener("submit", onSavePricing);

  async function load(): Promise<void> {
    try {
      const [logCfg, perfCfg, specCfg, wmCfg, priceCfg, userPromptCfg, sysPromptCfg, sttPromptCfg] = await Promise.all([
        adminGet<AdminLLMLoggingConfigDTO>("/api/admin/config/llm-logging"),
        adminGet<AdminPerfTrackingConfigDTO>("/api/admin/config/perf-tracking"),
        adminGet<AdminSpeculativeEvalConfigDTO>("/api/admin/config/speculative-eval"),
        adminGet<AdminWordMatchConfigDTO>("/api/admin/config/word-match"),
        adminGet<AdminPricingConfigDTO>("/api/admin/config/pricing"),
        adminGet<AdminUserPromptConfigDTO>("/api/admin/config/user-prompt"),
        adminGet<AdminSystemPromptConfigDTO>("/api/admin/config/system-prompt"),
        adminGet<AdminOpenAISTTPromptConfigDTO>("/api/admin/config/openai-stt-prompt"),
      ]);
      logCheckbox.checked = logCfg.enabled;
      logCheckbox.disabled = false;
      perfCheckbox.checked = perfCfg.enabled;
      perfCheckbox.disabled = false;
      specCheckbox.checked = specCfg.enabled;
      specCheckbox.disabled = false;
      wmCheckbox.checked = wmCfg.enabled;
      wmCheckbox.disabled = false;
      fillPricing(priceCfg);
      saveBtn.disabled = false;
      userPromptTA.value = userPromptCfg.template;
      userPromptTA.disabled = false;
      userPromptSaveBtn.disabled = false;
      sysPromptTA.value = sysPromptCfg.template;
      sysPromptTA.disabled = false;
      sysPromptSaveBtn.disabled = false;
      sttPromptTA.value = sttPromptCfg.template;
      sttPromptTA.disabled = false;
      sttPromptSaveBtn.disabled = false;
      status.textContent = "";
      status.remove();
    } catch (err) {
      if (err instanceof AdminError && (err.status === 401 || err.status === 403)) {
        status.textContent = "Not signed in — visit /login to authenticate, then refresh.";
        return;
      }
      status.textContent = errMessage(err, "load settings");
    }
  }

  void load();

  return () => {
    logCheckbox.removeEventListener("change", onToggleLogging);
    perfCheckbox.removeEventListener("change", onTogglePerf);
    specCheckbox.removeEventListener("change", onToggleSpec);
    wmCheckbox.removeEventListener("change", onToggleWordMatch);
    priceForm.removeEventListener("submit", onSavePricing);
    userPromptSaveBtn.removeEventListener("click", onSaveUserPrompt);
    sysPromptSaveBtn.removeEventListener("click", onSaveSysPrompt);
    importBtn.removeEventListener("click", onImportClick);
    filePicker.removeEventListener("change", onPickFile);
  };
}

function numInput(): HTMLInputElement {
  const i = document.createElement("input");
  i.type = "number";
  i.min = "0";
  i.step = "1";
  i.setAttribute("style", "padding: 4px 8px; border: 1px solid #d1d5db; border-radius: 4px; font-size: 14px; width: 100%; box-sizing: border-box;");
  return i;
}

// ── Content I/O import error helpers ──────────────────────────────────────
// Mirrors internal/admin/contentio.go::importIssuesResponse. The admin
// handler returns this JSON on a 422 when parser problems or missing-TTS
// were detected; the FE renders one numbered line per issue.

interface ImportIssueDTO {
  sheet?: string;
  row?: number;
  column_letter?: string;
  column?: string;
  message: string;
}

interface ImportIssuesEnvelope {
  code: string;
  message: string;
  issues?: ImportIssueDTO[];
}

interface ParsedImportError {
  title: string;
  issues: ImportIssueDTO[] | null;
  fallback: string;
}

function parseAdminImportError(err: unknown): ParsedImportError {
  if (err instanceof AdminError) {
    if (err.status === 401 || err.status === 403) {
      return {
        title: "Not signed in",
        issues: null,
        fallback: "Visit /login to authenticate, then refresh.",
      };
    }
    const env = err.jsonBody<ImportIssuesEnvelope>();
    if (env && Array.isArray(env.issues) && env.issues.length > 0) {
      return {
        title: titleForCode(env.code, env.issues.length),
        issues: env.issues,
        fallback: "",
      };
    }
    if (env && env.message) {
      return {
        title: titleForCode(env.code, 0),
        issues: null,
        fallback: env.message,
      };
    }
    return {
      title: `Import failed (HTTP ${err.status})`,
      issues: null,
      fallback: err.bodyText || err.message,
    };
  }
  return {
    title: "Import failed",
    issues: null,
    fallback: errMessage(err, "import bundle"),
  };
}

function titleForCode(code: string, count: number): string {
  const n = count > 0 ? ` (${count} ${count === 1 ? "issue" : "issues"})` : "";
  switch (code) {
    case "import_issues": return `Import failed${n}`;
    case "import_failed": return `Import failed${n}`;
    case "target_not_empty": return `Import failed — target not empty${n}`;
    case "sessions_reference_target": return `Import failed — game has live sessions${n}`;
    case "too_large": return "Bundle too large";
    case "bad_zip": return "Invalid zip file";
    case "missing_xlsx": return "Missing content.xlsx in bundle";
    case "path_traversal": return "Bundle contains an unsafe path";
    default: return code ? `Import failed: ${code}${n}` : `Import failed${n}`;
  }
}

function formatIssue(it: ImportIssueDTO): string {
  const parts: string[] = [];
  if (it.sheet && it.row) {
    parts.push(`${it.sheet} sheet, Row ${it.row}`);
  } else if (it.sheet) {
    parts.push(`${it.sheet} sheet`);
  } else if (it.row) {
    parts.push(`Row ${it.row}`);
  }
  if (it.column_letter || it.column) {
    let col: string;
    if (it.column_letter && it.column) {
      col = `Column ${it.column_letter} (${it.column})`;
    } else if (it.column_letter) {
      col = `Column ${it.column_letter}`;
    } else {
      col = `Column ${it.column}`;
    }
    parts.push(col);
  }
  const head = parts.join(", ");
  return head ? `${head}: ${it.message}` : it.message;
}
